import React, { memo, useState, useEffect } from 'react';
import { Shape } from '../../types/Shape';
import ArrowEndpointHandles from './ArrowEndpointHandles';

interface SVGArrowProps {
  shape: Shape;
  isSelected: boolean;
  showEndpointHandles?: boolean;
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing?: boolean;
  currentTool?: string;
  allShapes?: Shape[];
  onClick?: (shapeId: string, e: React.MouseEvent) => void;
  onContextMenu?: (shapeId: string, e: React.MouseEvent) => void;
  onDragStart?: (shapeId: string) => void;
  onDragMove?: (shapeId: string, dx: number, dy: number) => void;
  onDragEnd?: (shapeId: string) => void;
  onStartPointMove?: (arrowId: string, newX: number, newY: number) => void;
  onEndPointMove?: (arrowId: string, newX: number, newY: number) => void;
  onStartPointEnd?: (arrowId: string, finalX: number, finalY: number) => void;
  onEndPointEnd?: (arrowId: string, finalX: number, finalY: number) => void;
  onSnapUpdate?: (snappedPoint: { x: number; y: number } | null) => void;
  onEndpointDraggingChange?: (isDragging: boolean) => void;
  onCurveControlMove?: (arrowId: string, offset: { x: number; y: number }) => void;
  onWaypointMove?: (arrowId: string, waypointIndex: number, x: number, y: number) => void;
  onWaypointAdd?: (arrowId: string, x: number, y: number, insertIndex: number) => void;
  onWaypointDelete?: (arrowId: string, waypointIndex: number) => void;
}

const SVGArrow: React.FC<SVGArrowProps> = ({
  shape,
  isSelected,
  showEndpointHandles = false,
  stagePos,
  stageScale,
  isDrawing = false,
  currentTool = 'select',
  allShapes = [],
  onClick,
  onContextMenu,
  onDragStart,
  onDragMove,
  onDragEnd,
  onStartPointMove,
  onEndPointMove,
  onStartPointEnd,
  onEndPointEnd,
  onSnapUpdate,
  onEndpointDraggingChange,
  onCurveControlMove,
  onWaypointMove,
  onWaypointAdd,
  onWaypointDelete,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isControlDragging, setIsControlDragging] = useState(false);
  const [controlDragStart, setControlDragStart] = useState({ x: 0, y: 0 });
  const [draggingWaypointIndex, setDraggingWaypointIndex] = useState<number | null>(null);

  // 화살표의 시작점과 끝점 계산
  const startX = shape.x;
  const startY = shape.y;
  const endX = shape.x + (shape.width || 0);
  const endY = shape.y + (shape.height || 0);
  const originalWaypoints = shape.waypoints || [];

  // 곡선인데 웨이포인트가 없으면 가상의 웨이포인트 계산
  const getVirtualWaypoint = () => {
    const arrowWidth = (shape.width || 0);
    const arrowHeight = (shape.height || 0);
    const midX = startX + arrowWidth / 2;
    const midY = startY + arrowHeight / 2;
    const dx = arrowWidth;
    const dy = arrowHeight;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const offsetRatio = 0.2;

    if (distance > 0) {
      const perpX = -dy / distance;
      const perpY = dx / distance;
      const offset = distance * offsetRatio;
      return { x: midX + perpX * offset, y: midY + perpY * offset };
    }
    return { x: midX, y: midY };
  };

  // 곡선이고 웨이포인트가 없으면 가상 웨이포인트 사용
  const waypoints = (shape.isCurved && originalWaypoints.length === 0)
    ? [getVirtualWaypoint()]
    : originalWaypoints;

  // 가상 웨이포인트 사용 여부 플래그
  const isUsingVirtualWaypoint = shape.isCurved && originalWaypoints.length === 0;

  // 모든 점들 (시작점, 웨이포인트들, 끝점) 수집
  const allPoints = [
    { x: startX, y: startY },
    ...waypoints,
    { x: endX, y: endY }
  ];

  // 화면 좌표로 변환
  const screenPoints = allPoints.map(p => ({
    x: p.x * stageScale + stagePos.x,
    y: p.y * stageScale + stagePos.y
  }));

  const screenStartX = screenPoints[0].x;
  const screenStartY = screenPoints[0].y;
  const screenEndX = screenPoints[screenPoints.length - 1].x;
  const screenEndY = screenPoints[screenPoints.length - 1].y;

  // SVG viewBox 계산 (모든 점을 포함)
  const allScreenX = screenPoints.map(p => p.x);
  const allScreenY = screenPoints.map(p => p.y);
  const minX = Math.min(...allScreenX);
  const minY = Math.min(...allScreenY);
  const maxX = Math.max(...allScreenX);
  const maxY = Math.max(...allScreenY);
  const padding = 20; // 화살표 머리 공간

  const svgWidth = maxX - minX + padding * 2;
  const svgHeight = maxY - minY + padding * 2;

  // SVG 내부 좌표로 변환
  const localPoints = screenPoints.map(p => ({
    x: p.x - minX + padding,
    y: p.y - minY + padding
  }));
  const localStartX = localPoints[0].x;
  const localStartY = localPoints[0].y;
  const localEndX = localPoints[localPoints.length - 1].x;
  const localEndY = localPoints[localPoints.length - 1].y;

  // 곡선 화살표를 위한 제어점 계산 (3차 베지어 곡선)
  // curveOffset이 있으면 그것을 사용, 없으면 curvature 비율 사용 (하위 호환성)
  const getCurveControlPoint = () => {
    const midX = (localStartX + localEndX) / 2;
    const midY = (localStartY + localEndY) / 2;

    if (shape.curveOffset) {
      // curveOffset은 캔버스 좌표계에서의 오프셋 (stageScale 적용)
      return {
        x: midX + shape.curveOffset.x * stageScale,
        y: midY + shape.curveOffset.y * stageScale
      };
    }

    // 하위 호환성: curvature 비율 사용
    const dx = localEndX - localStartX;
    const dy = localEndY - localStartY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance === 0) {
      return { x: midX, y: midY };
    }

    const curvatureRatio = shape.curvature ?? 0.25;
    const curvature = distance * curvatureRatio;
    const perpX = -dy / distance;
    const perpY = dx / distance;

    return {
      x: midX + perpX * curvature,
      y: midY + perpY * curvature
    };
  };

  const getCurvedPath = () => {
    const dx = localEndX - localStartX;
    const dy = localEndY - localStartY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance === 0) {
      return `M ${localStartX} ${localStartY} L ${localEndX} ${localEndY}`;
    }

    // 제어점 계산
    const controlPoint = getCurveControlPoint();

    // 2차 베지어 곡선 (Q) - 하나의 제어점으로 자연스러운 곡선
    return `M ${localStartX} ${localStartY} Q ${controlPoint.x} ${controlPoint.y} ${localEndX} ${localEndY}`;
  };

  // 직선 화살표 경로
  const getStraightPath = () => {
    return `M ${localStartX} ${localStartY} L ${localEndX} ${localEndY}`;
  };

  // 웨이포인트를 통과하는 부드러운 곡선 경로 (Catmull-Rom to Bezier)
  const getWaypointPath = () => {
    if (localPoints.length < 2) return '';
    if (localPoints.length === 2) {
      return `M ${localPoints[0].x} ${localPoints[0].y} L ${localPoints[1].x} ${localPoints[1].y}`;
    }

    // 가상의 경계점 추가: 시작점 이전과 끝점 이후에 가상점 생성
    // 이렇게 하면 웨이포인트가 1개일 때도 2개 이상일 때와 동일한 부드러운 곡선 생성
    const first = localPoints[0];
    const second = localPoints[1];
    const secondLast = localPoints[localPoints.length - 2];
    const last = localPoints[localPoints.length - 1];

    // 시작 전 가상점: 시작점에서 두 번째 점 방향의 반대로 연장
    const virtualStart = {
      x: first.x - (second.x - first.x),
      y: first.y - (second.y - first.y)
    };

    // 끝 후 가상점: 끝점에서 끝에서 두 번째 점 방향의 반대로 연장
    const virtualEnd = {
      x: last.x - (secondLast.x - last.x),
      y: last.y - (secondLast.y - last.y)
    };

    // 가상점 포함한 확장된 점 배열
    const extendedPoints = [virtualStart, ...localPoints, virtualEnd];

    // Catmull-Rom 스플라인을 베지어 곡선으로 변환
    let path = `M ${localPoints[0].x} ${localPoints[0].y}`;

    for (let i = 0; i < localPoints.length - 1; i++) {
      // 확장된 배열에서 인덱스 조정 (+1 because of virtualStart)
      const p0 = extendedPoints[i];     // virtualStart or previous point
      const p1 = extendedPoints[i + 1]; // current point
      const p2 = extendedPoints[i + 2]; // next point
      const p3 = extendedPoints[i + 3]; // next next point or virtualEnd

      // Catmull-Rom to Cubic Bezier 변환
      const tension = 6; // 곡선의 부드러움 (낮을수록 더 곡선)
      const cp1x = p1.x + (p2.x - p0.x) / tension;
      const cp1y = p1.y + (p2.y - p0.y) / tension;
      const cp2x = p2.x - (p3.x - p1.x) / tension;
      const cp2y = p2.y - (p3.y - p1.y) / tension;

      path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`;
    }

    return path;
  };

  // 웨이포인트가 있으면 웨이포인트 경로, 없으면 곡선/직선
  const pathData = waypoints.length > 0
    ? getWaypointPath()
    : (shape.isCurved ? getCurvedPath() : getStraightPath());

  // 드래그 핸들러
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;

    e.preventDefault(); // 텍스트 선택 방지
    e.stopPropagation();

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });

    if (onDragStart) {
      onDragStart(shape.id);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault(); // 텍스트 선택 방지

      const dx = (e.clientX - dragStart.x) / stageScale;
      const dy = (e.clientY - dragStart.y) / stageScale;

      if (onDragMove) {
        onDragMove(shape.id, dx, dy);
      }

      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (onDragEnd) {
        onDragEnd(shape.id);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, stageScale, shape.id, onDragMove, onDragEnd]);

  // 제어점 드래그 핸들러
  useEffect(() => {
    if (!isControlDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      // 화살표 중간점 (캔버스 좌표)
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;

      // 마우스 위치를 캔버스 좌표로 변환
      const mouseCanvasX = (e.clientX - stagePos.x) / stageScale;
      const mouseCanvasY = (e.clientY - stagePos.y) / stageScale;

      // 중간점에서의 오프셋 계산
      const offsetX = mouseCanvasX - midX;
      const offsetY = mouseCanvasY - midY;

      if (onCurveControlMove) {
        onCurveControlMove(shape.id, { x: offsetX, y: offsetY });
      }
    };

    const handleMouseUp = () => {
      setIsControlDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isControlDragging, startX, endX, startY, endY, stagePos, stageScale, shape.id, onCurveControlMove]);

  const handleControlMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    setIsControlDragging(true);
    setControlDragStart({ x: e.clientX, y: e.clientY });
  };

  // 가상 웨이포인트가 실제로 추가되었는지 추적
  const [virtualWaypointAdded, setVirtualWaypointAdded] = useState(false);

  // 웨이포인트 드래그 핸들러
  useEffect(() => {
    if (draggingWaypointIndex === null) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault(); // 텍스트 선택 방지

      // 마우스 위치를 캔버스 좌표로 변환
      const canvasX = (e.clientX - stagePos.x) / stageScale;
      const canvasY = (e.clientY - stagePos.y) / stageScale;

      // 가상 웨이포인트를 드래그하는 경우 먼저 실제로 추가
      if (isUsingVirtualWaypoint && !virtualWaypointAdded) {
        if (onWaypointAdd) {
          onWaypointAdd(shape.id, canvasX, canvasY, 0);
          setVirtualWaypointAdded(true);
        }
      } else if (onWaypointMove) {
        onWaypointMove(shape.id, draggingWaypointIndex, canvasX, canvasY);
      }
    };

    const handleMouseUp = () => {
      setDraggingWaypointIndex(null);
      setVirtualWaypointAdded(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingWaypointIndex, stagePos, stageScale, shape.id, onWaypointMove, onWaypointAdd, isUsingVirtualWaypoint, virtualWaypointAdded]);

  const handleWaypointMouseDown = (index: number) => (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault(); // 텍스트 선택 방지
    e.stopPropagation();
    setDraggingWaypointIndex(index);
  };

  const handleWaypointRightClick = (index: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onWaypointDelete) {
      onWaypointDelete(shape.id, index);
    }
  };

  // 화살표 선을 더블클릭하면 웨이포인트 추가
  const handleLineDoubleClick = (e: React.MouseEvent) => {
    if (!onWaypointAdd) return;
    e.stopPropagation();

    // 마우스 위치를 캔버스 좌표로 변환
    const canvasX = (e.clientX - stagePos.x) / stageScale;
    const canvasY = (e.clientY - stagePos.y) / stageScale;

    // 가장 가까운 선분 찾기
    let bestIndex = 0;
    let bestDistance = Infinity;

    for (let i = 0; i < allPoints.length - 1; i++) {
      const p1 = allPoints[i];
      const p2 = allPoints[i + 1];

      // 점에서 선분까지의 거리 계산
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const lengthSq = dx * dx + dy * dy;

      let t = 0;
      if (lengthSq > 0) {
        t = Math.max(0, Math.min(1, ((canvasX - p1.x) * dx + (canvasY - p1.y) * dy) / lengthSq));
      }

      const nearestX = p1.x + t * dx;
      const nearestY = p1.y + t * dy;
      const distance = Math.sqrt((canvasX - nearestX) ** 2 + (canvasY - nearestY) ** 2);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    // 웨이포인트 추가 (시작점 이후 인덱스에 삽입)
    onWaypointAdd(shape.id, canvasX, canvasY, bestIndex);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!isDragging && onClick) {
      onClick(shape.id, e);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onContextMenu) {
      onContextMenu(shape.id, e);
    }
  };

  const markerId = `arrow-${shape.id}`;
  const markerStartId = `arrow-start-${shape.id}`;

  return (
    <>
    <svg
      style={{
        position: 'absolute',
        left: minX - padding,
        top: minY - padding,
        width: svgWidth,
        height: svgHeight,
        overflow: 'visible',
        pointerEvents: 'none', // SVG 컨테이너는 이벤트 무시, path만 받음
        zIndex: (shape.order ?? 1) * 2,
      }}
    >
      {/* 화살표 머리 정의 */}
      <defs>
        {/* 끝점 화살표 */}
        {shape.endArrow && (
          <marker
            id={markerId}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth={6 * stageScale}
            markerHeight={6 * stageScale}
            orient="auto"
          >
            <path
              d="M 0 0 L 10 5 L 0 10 z"
              fill={shape.stroke || '#000000'}
            />
          </marker>
        )}
        
        {/* 시작점 화살표 */}
        {shape.startArrow && (
          <marker
            id={markerStartId}
            viewBox="0 0 10 10"
            refX="1"
            refY="5"
            markerWidth={6 * stageScale}
            markerHeight={6 * stageScale}
            orient="auto"
          >
            <path
              d="M 10 0 L 0 5 L 10 10 z"
              fill={shape.stroke || '#000000'}
            />
          </marker>
        )}
      </defs>

      {/* 투명한 넓은 클릭 영역 */}
      <path
        d={pathData}
        stroke="transparent"
        strokeWidth={Math.max(10, (shape.strokeWidth || 2) + 8) * stageScale}
        fill="none"
        style={{
          pointerEvents: isDrawing ? 'none' : 'stroke', // 그리기 중에는 무시, 평상시에는 선만 클릭 가능
          cursor: isDragging ? 'grabbing' : (currentTool === 'select' ? 'grab' : 'default'),
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onDoubleClick={handleLineDoubleClick}
        onContextMenu={handleContextMenu}
      />

      {/* 선택 표시 */}
      {isSelected && (
        <path
          d={pathData}
          stroke="#3B82F6"
          strokeWidth={Math.max(6, (shape.strokeWidth || 2) + 4) * stageScale}
          fill="none"
          opacity="0.3"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* 실제 화살표 경로 */}
      <path
        d={pathData}
        stroke={shape.stroke || '#000000'}
        strokeWidth={(shape.strokeWidth || 2) * stageScale}
        fill="none"
        markerEnd={shape.endArrow ? `url(#${markerId})` : undefined}
        markerStart={shape.startArrow ? `url(#${markerStartId})` : undefined}
        style={{
          pointerEvents: 'none',
        }}
      />

      {/* 웨이포인트 핸들 */}
      {waypoints.length > 0 && (isSelected || isDrawing) && localPoints.slice(1, -1).map((point, index) => (
        <circle
          key={`waypoint-${index}`}
          cx={point.x}
          cy={point.y}
          r={7}
          fill={draggingWaypointIndex === index ? '#DC2626' : '#EF4444'}
          stroke="white"
          strokeWidth={2}
          style={{
            pointerEvents: isDrawing ? 'none' : 'auto',
            cursor: isDrawing ? 'default' : (draggingWaypointIndex === index ? 'grabbing' : 'grab'),
          }}
          onMouseDown={isDrawing ? undefined : handleWaypointMouseDown(index)}
          onContextMenu={isDrawing ? undefined : handleWaypointRightClick(index)}
        />
      ))}
    </svg>
    
    {/* 끝점 조절 핸들 */}
    {showEndpointHandles && !isDrawing && (
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
        <div style={{ pointerEvents: 'auto' }}>
          <ArrowEndpointHandles
            arrow={shape}
            stagePos={stagePos}
            stageScale={stageScale}
            allShapes={allShapes}
            onStartMove={onStartPointMove}
            onEndMove={onEndPointMove}
            onStartEnd={onStartPointEnd}
            onEndEnd={onEndPointEnd}
            onSnapUpdate={onSnapUpdate}
            onDraggingChange={onEndpointDraggingChange}
          />
        </div>
      </div>
    )}
    </>
  );
};

export default memo(SVGArrow);

